import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, type DefaultValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Alert from '@/components/Alert/Alert'
import DateRangePicker from '@/components/DateRangePicker/DateRangePicker'
import FormField from '@/components/FormField'
import {
  FormButtons,
  FormLayout,
  FormSectionFields,
  FormSectionHeader
} from '@/components/common/FormGrid/FormGrid'
import useExternalErrors from '@/hooks/useExternalErrors'
import { type FormError } from '@/types/form-error'

import styles from './SchoolYearForm.module.scss'
import {
  schoolYearPayload,
  type SchoolYearFormPayload
} from '../constants/school-year-payload'

type SchooYearFormProps = {
  defaultValues: DefaultValues<SchoolYearFormPayload>
  errors?: FormError<SchoolYearFormPayload>[]
  onSubmit: (data: SchoolYearFormPayload) => void
}

const SchooYearForm = (props: SchooYearFormProps) => {
  const { t } = useTranslation('schoolSettings')

  const form = useForm<SchoolYearFormPayload>({
    resolver: zodResolver(schoolYearPayload),
    mode: 'all',
    defaultValues: props.defaultValues
  })

  useExternalErrors(props.errors, form)

  return (
    <FormLayout form={form} onSubmit={props.onSubmit}>
      {form.formState.errors.root ? (
        <Alert
          message={t('error.overlap-with-existing-one')}
          variant="error"
          className={styles.alert}
        />
      ) : null}

      <FormSectionFields>
        <div className={styles.section}>
          <FormSectionHeader>{t('header.semester-1')}</FormSectionHeader>
          <FormField
            control={form.control}
            id="first-semester"
            label={t('label.first-semester-dates')}
            required
            name="firstSemester"
            render={({ inputProps }) => (
              <DateRangePicker
                {...inputProps}
                onChange={value => {
                  if (value && inputProps.onChange) inputProps.onChange(value)

                  const secondSemester = form.getValues('secondSemester')

                  const isFirstSemesterAndSecondSemesterFilled =
                    value?.from &&
                    value.to &&
                    secondSemester.from &&
                    secondSemester.to
                  if (isFirstSemesterAndSecondSemesterFilled)
                    form.trigger('secondSemester')
                }}
                clearable
                onClear={() => {
                  if (inputProps.onChange) inputProps.onChange({})
                  form.trigger('secondSemester')
                }}
              />
            )}
          />
        </div>

        <div className={styles.section}>
          <FormSectionHeader>{t('header.semester-2')}</FormSectionHeader>
          <FormField
            control={form.control}
            id="second-semester"
            label={t('label.second-semester-dates')}
            required
            name="secondSemester"
            render={({ inputProps }) => (
              <DateRangePicker
                {...inputProps}
                onChange={value => {
                  if (value && inputProps.onChange) inputProps.onChange(value)
                }}
                clearable
                onClear={() => {
                  if (inputProps.onChange) inputProps.onChange({})
                }}
              />
            )}
          />
        </div>
      </FormSectionFields>

      <FormButtons />
    </FormLayout>
  )
}

export default SchooYearForm
